import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useUser, resetPassword } from '@/utils/useUser';
import { SEOMeta } from '@/templates/SEOMeta';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', content: '' });
    const router = useRouter();
    const { user, forgotPassword } = useUser();
    let access_token = null;

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({});

        const { error } = await forgotPassword(email);
        if (error) {
            setMessage({ type: 'error', content: error.message });
        } else {
            setMessage({
                type: 'success',
                content: 'Check your email for the password reset link.'
            });
        }
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({});

        const { error } = await resetPassword(access_token, password);
        if (error) {
            setMessage({ type: 'error', content: error.message });
        } else {
            setMessage({
                type: 'success',
                content: 'Your password has been reset'
            });
            setTimeout(function () {
                router.push('/dashboard');
            }, 3000);
        }
        setLoading(false);
    };

    if (router?.asPath?.indexOf("?passwordReset=true&access_token=") > 0) {
        access_token = router?.asPath?.split("&access_token=")[1].split("&")[0];
    }

    useEffect(() => {
        if (user && router?.asPath?.indexOf("?passwordReset=true&access_token=") === -1) {
            router.replace('/dashboard');
        }
    }, [router, user]);

    return (
        <>
            <SEOMeta title="Reset Password | Partner Portal" noindex />
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4">
                <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-8 animate-in fade-in duration-700">
                    <div className="text-center space-y-3">
                        <div className="relative inline-block">
                            <div className="h-24 w-24 flex items-center justify-center transition-all duration-500 hover:scale-110 -rotate-6">
                                <img
                                    src="/business_signin.webp"
                                    alt="Reset Password"
                                    className="w-full h-full object-contain"
                                    loading="eager"
                                    draggable="false"
                                />
                            </div>
                        </div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-tight">
                            {access_token ? "Set New Password" : "Reset Password"}
                        </h1>
                        <p className="text-gray-400 text-sm font-medium px-4 leading-relaxed">
                            {access_token
                                ? "Choose a strong password for your account"
                                : "We'll send you a link to get back into your account"}
                        </p>
                    </div>

                    {message.content && (
                        <div
                            className={`p-5 rounded-2xl border-2 shadow-sm animate-in slide-in-from-top-2 duration-500 ${message.type === "error"
                                    ? "border-red-200 bg-red-50 text-red-700 ring-1 ring-red-100"
                                    : "border-emerald-200 bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100"
                                }`}
                        >
                            <div className="flex items-center justify-center gap-2 text-center">
                                <span className="text-lg">
                                    {message.type === "error" ? "⚠️" : "✉️"}
                                </span>
                                <p className="font-bold text-sm tracking-normal">
                                    {message.content}
                                </p>
                            </div>
                        </div>
                    )}

                    <form onSubmit={access_token ? handleResetPassword : handleForgotPassword} className="space-y-6">
                        <div className="space-y-3">
                            {access_token ? (
                                <input
                                    id="password"
                                    type="password"
                                    required
                                    className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-gray-100 text-gray-900 focus:ring-0 focus:border-blue-600 transition-all outline-none text-base placeholder:text-gray-300"
                                    placeholder="Your new secure password"
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            ) : (
                                <input
                                    id="email"
                                    type="email"
                                    required
                                    className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-gray-100 text-gray-900 focus:ring-0 focus:border-blue-600 transition-all outline-none text-base placeholder:text-gray-300"
                                    placeholder="Work email address"
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full py-5 text-base font-black rounded-2xl transition-all shadow-xl hover:shadow-2xl active:scale-[0.97] uppercase tracking-wider bg-blue-600 hover:bg-blue-700 text-white shadow-blue-100 ${loading ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                        >
                            {loading ? "Processing..." : (access_token ? "Update Password" : "Send Reset Link")}
                        </button>

                        <div className="text-center pt-2">
                            <Link
                                href="/signin"
                                className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors"
                            >
                                Back to sign in
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;
